import { useState, useEffect } from 'react';
import { dgiiApi } from '../../api/dgii';
import { useAuth } from '../../contexts/AuthContext';
import {
	PlusIcon,
	PencilIcon,
	TrashIcon,
	XMarkIcon,
	CheckIcon,
	MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

export default function GestionActividadesEconomicas() {
	const { isAdmin } = useAuth();
	const [actividades, setActividades] = useState([]);
	const [grupos, setGrupos] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedGrupo, setSelectedGrupo] = useState('');
	const [showModal, setShowModal] = useState(false);
	const [editingActividad, setEditingActividad] = useState(null);
	const [formData, setFormData] = useState({
		literal_grupo: '',
		descripcion_grupo: '',
		codigo: '',
		descripcion: '',
		activa: true
	});
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (isAdmin) {
			cargarGrupos();
			buscarActividades();
		}
	}, [isAdmin]);

	useEffect(() => {
		buscarActividades();
	}, [searchTerm, selectedGrupo]);

	const cargarGrupos = async () => {
		try {
			const data = await dgiiApi.getGruposEconomicos();
			setGrupos(Array.isArray(data) ? data : []);
		} catch (err) {
			console.error('Error cargando grupos:', err);
		}
	};

	const buscarActividades = async () => {
		setLoading(true);
		try {
			const data = await dgiiApi.getActividadesEconomicas(searchTerm, selectedGrupo);
			setActividades(Array.isArray(data) ? data : []);
		} catch (err) {
			console.error('Error buscando actividades:', err);
			setActividades([]);
		} finally {
			setLoading(false);
		}
	};

	const handleOpenModal = (actividad = null) => {
		if (actividad) {
			setEditingActividad(actividad);
			setFormData({
				literal_grupo: actividad.literal_grupo || '',
				descripcion_grupo: actividad.descripcion_grupo || '',
				codigo: actividad.codigo || '',
				descripcion: actividad.descripcion || '',
				activa: actividad.activa !== false
			});
		} else {
			setEditingActividad(null);
			setFormData({
				literal_grupo: '',
				descripcion_grupo: '',
				codigo: '',
				descripcion: '',
				activa: true
			});
		}
		setErrors({});
		setShowModal(true);
	};

	const handleCloseModal = () => {
		setShowModal(false);
		setEditingActividad(null);
		setFormData({
			literal_grupo: '',
			descripcion_grupo: '',
			codigo: '',
			descripcion: '',
			activa: true
		});
		setErrors({});
	};

	const validateForm = () => {
		const newErrors = {};
		
		// Literal del grupo es opcional, pero si se proporciona debe ser válido
		if (formData.literal_grupo && !/^[A-Q]$/.test(formData.literal_grupo.toUpperCase())) {
			newErrors.literal_grupo = 'El literal del grupo debe ser una letra entre A y Q';
		}
		
		// Descripción del grupo es opcional si no hay literal
		if (formData.literal_grupo && (!formData.descripcion_grupo || formData.descripcion_grupo.trim().length < 3)) {
			newErrors.descripcion_grupo = 'La descripción del grupo es requerida cuando se especifica un literal (mínimo 3 caracteres)';
		}
		
		if (!formData.codigo || !/^\d{1,6}$/.test(formData.codigo)) {
			newErrors.codigo = 'El código debe ser numérico de 1 a 6 dígitos';
		}
		
		if (!formData.descripcion || formData.descripcion.trim().length < 3) {
			newErrors.descripcion = 'La descripción es requerida (mínimo 3 caracteres)';
		}
		
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (!validateForm()) {
			return;
		}

		try {
			const actividadData = {
				...formData,
				literal_grupo: formData.literal_grupo.toUpperCase()
			};

			if (editingActividad) {
				await dgiiApi.updateActividadEconomica(editingActividad.id, actividadData);
				window.alert('Actividad económica actualizada exitosamente');
			} else {
				await dgiiApi.createActividadEconomica(actividadData);
				window.alert('Actividad económica creada exitosamente');
			}

			handleCloseModal();
			buscarActividades();
		} catch (err) {
			console.error('Error guardando actividad:', err);
			const errorMessage = err.response?.data?.detail || err.response?.data?.codigo?.[0] || 'Error al guardar la actividad económica';
			window.alert(errorMessage);
		}
	};

	const handleDelete = async (id) => {
		if (!window.confirm('¿Está seguro de que desea eliminar esta actividad económica?')) {
			return;
		}

		try {
			await dgiiApi.deleteActividadEconomica(id);
			window.alert('Actividad económica eliminada exitosamente');
			buscarActividades();
		} catch (err) {
			console.error('Error eliminando actividad:', err);
			window.alert('Error al eliminar la actividad económica');
		}
	};

	if (!isAdmin) {
		return (
			<div className="p-6 text-center text-gray-500">
				No tiene permisos para acceder a esta sección.
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 mb-2">Gestión de Actividades Económicas</h1>
				<p className="text-gray-600">Administre el catálogo de actividades económicas según clasificación DGII</p>
			</div>

			{/* Filtros y búsqueda */}
			<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
				<div className="flex flex-col sm:flex-row gap-4">
					<div className="flex-1">
						<label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
						<div className="relative">
							<MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
							<input
								type="text"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder="Buscar por código o descripción..."
								className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
							/>
						</div>
					</div>
					<div className="sm:w-48">
						<label className="block text-sm font-medium text-gray-700 mb-1">Grupo</label>
						<select
							value={selectedGrupo}
							onChange={(e) => setSelectedGrupo(e.target.value)}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
						>
							<option value="">Todos los grupos</option>
							{grupos.map((grupo) => (
								<option key={grupo.literal} value={grupo.literal}>
									{grupo.literal} - {grupo.descripcion}
								</option>
							))}
						</select>
					</div>
					<div className="flex items-end">
						<button
							onClick={() => handleOpenModal()}
							className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
						>
							<PlusIcon className="h-5 w-5" />
							Agregar Actividad
						</button>
					</div>
				</div>
			</div>

			{/* Tabla de actividades */}
			<div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
				{loading ? (
					<div className="p-8 text-center text-gray-500">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
						Cargando...
					</div>
				) : actividades.length === 0 ? (
					<div className="p-8 text-center text-gray-500">
						No se encontraron actividades económicas
					</div>
				) : (
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Grupo</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Código</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Descripción</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Estado</th>
								<th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Acciones</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{actividades.map((actividad) => (
								<tr key={actividad.id} className="hover:bg-gray-50">
									<td className="px-4 py-3 whitespace-nowrap">
										<div>
											<span className="text-sm font-semibold text-gray-900">{actividad.literal_grupo}</span>
											<p className="text-xs text-gray-500">{actividad.descripcion_grupo}</p>
										</div>
									</td>
									<td className="px-4 py-3 whitespace-nowrap">
										<span className="text-sm font-medium text-primary">{actividad.codigo}</span>
									</td>
									<td className="px-4 py-3">
										<span className="text-sm text-gray-900">{actividad.descripcion}</span>
									</td>
									<td className="px-4 py-3 whitespace-nowrap">
										<span className={`px-2 py-1 text-xs font-medium rounded-full ${
											actividad.activa ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
										}`}>
											{actividad.activa ? 'Activa' : 'Inactiva'}
										</span>
									</td>
									<td className="px-4 py-3 whitespace-nowrap text-right">
										<div className="flex items-center justify-end gap-2">
											<button
												onClick={() => handleOpenModal(actividad)}
												className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
												title="Editar"
											>
												<PencilIcon className="h-4 w-4" />
											</button>
											<button
												onClick={() => handleDelete(actividad.id)}
												className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
												title="Eliminar"
											>
												<TrashIcon className="h-4 w-4" />
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{/* Modal de crear/editar */}
			{showModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
						<div className="p-6 border-b border-gray-200">
							<div className="flex items-center justify-between">
								<h2 className="text-xl font-bold text-gray-900">
									{editingActividad ? 'Editar Actividad Económica' : 'Nueva Actividad Económica'}
								</h2>
								<button
									onClick={handleCloseModal}
									className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
								>
									<XMarkIcon className="h-5 w-5" />
								</button>
							</div>
						</div>

						<form onSubmit={handleSubmit} className="p-6 space-y-4">
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Literal del Grupo
									</label>
									<input
										type="text"
										value={formData.literal_grupo}
										onChange={(e) => setFormData({ ...formData, literal_grupo: e.target.value.toUpperCase() })}
										placeholder="A-Q (Opcional)"
										maxLength={1}
										className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
											errors.literal_grupo ? 'border-red-500' : 'border-gray-300'
										}`}
									/>
									{errors.literal_grupo && (
										<p className="mt-1 text-sm text-red-600">{errors.literal_grupo}</p>
									)}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Descripción del Grupo
									</label>
									<input
										type="text"
										value={formData.descripcion_grupo}
										onChange={(e) => setFormData({ ...formData, descripcion_grupo: e.target.value })}
										placeholder="Ej: Agricultura, ganadería... (Opcional)"
										className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
											errors.descripcion_grupo ? 'border-red-500' : 'border-gray-300'
										}`}
									/>
									{errors.descripcion_grupo && (
										<p className="mt-1 text-sm text-red-600">{errors.descripcion_grupo}</p>
									)}
								</div>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Código <span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									value={formData.codigo}
									onChange={(e) => {
										const value = e.target.value.replace(/\D/g, '').slice(0, 6);
										setFormData({ ...formData, codigo: value });
									}}
									placeholder="0001 o 151123 (máximo 6 dígitos)"
									maxLength={6}
									className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
										errors.codigo ? 'border-red-500' : 'border-gray-300'
									}`}
								/>
								{errors.codigo && (
									<p className="mt-1 text-sm text-red-600">{errors.codigo}</p>
								)}
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Descripción <span className="text-red-500">*</span>
								</label>
								<textarea
									value={formData.descripcion}
									onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
									placeholder="Descripción de la actividad económica..."
									rows={3}
									className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
										errors.descripcion ? 'border-red-500' : 'border-gray-300'
									}`}
								/>
								{errors.descripcion && (
									<p className="mt-1 text-sm text-red-600">{errors.descripcion}</p>
								)}
							</div>

							<div className="flex items-center gap-3">
								<input
									type="checkbox"
									id="activa"
									checked={formData.activa}
									onChange={(e) => setFormData({ ...formData, activa: e.target.checked })}
									className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
								/>
								<label htmlFor="activa" className="text-sm font-medium text-gray-700">
									Actividad activa
								</label>
							</div>

							<div className="flex items-center gap-3 pt-4 border-t border-gray-200">
								<button
									type="button"
									onClick={handleCloseModal}
									className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
								>
									Cancelar
								</button>
								<button
									type="submit"
									className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
								>
									<CheckIcon className="h-5 w-5" />
									{editingActividad ? 'Actualizar' : 'Crear'}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}
